import React, {useContext, useEffect, useState} from "react";
import {createTheme, Tab, Tabs, ThemeProvider} from "@mui/material";
import {translateService} from "../../../services/TranslateService";
import {basePrice} from "../../MakeReservation/pricingCalculations";
import useFilter, {SortOptions} from "../Filter";
import {IVehicleSearch} from "../../../interfaces/VehicleSearch";
import {DataContext} from "../../../contexts/dataContext";

const yellowTheme = createTheme({
    palette: {
        primary: {
            main: '#fbb040'
        },
    },
});

export const SearchTab = () => { // for normal reservation tab sorting only, heavier functionality with all filters included
    const { sortVehicles, lowestPrice, lowestDistance, highestTrunkVolume } = useFilter();
    const { filteredParams, acrissCodeFromUrl, modelId } = useContext(DataContext);
    const [value, setValue] = useState( "distance");

    const handleChange = (event: React.ChangeEvent<{}>, newValue: string) => {
        setValue(newValue);
        if(newValue === 'distance') {
            sortVehicles('distance' as SortOptions)
        }
        if(newValue === 'price') {
            sortVehicles('price' as SortOptions)
        }
        if(newValue === 'trunkvolume') {
            sortVehicles('trunkvolume' as SortOptions)
        }
    };

    useEffect(() => {
        // if user changes any filter option, resort order of results
        if (!acrissCodeFromUrl && !modelId ) {
            sortVehicles(value as SortOptions);
        }

        // Note: using sort options to trigger this useEffect is not enough as...
        // the value of the tab won't change but the list order will
    }, [filteredParams, acrissCodeFromUrl, modelId])


    return (
        <div className={'mt-3'}>
            <ThemeProvider theme={yellowTheme}>
                <Tabs value={value} onChange={handleChange} variant="fullWidth"  TabIndicatorProps={{ style: { backgroundColor: "white" } }}>
                    <Tab label={`${translateService.translate('vehicleSearch.closest')} ${lowestDistance} km`} value="distance" classes={{ selected: 'tabActive', root: 'tabInactive' }} sx={{ textTransform: 'none' }}/>
                    <Tab label={`${translateService.translate('vehicleSearch.cheapest')} ${lowestPrice} €`} value="price" classes={{ selected: 'tabActive', root: 'tabInactive' }} sx={{ textTransform: 'none' }} />
                    <Tab label={`${translateService.translate('vehicleSearch.trunkSize')} ${highestTrunkVolume} m³`} value="trunkvolume" classes={{ selected: 'tabActive', root: 'tabInactive' }} sx={{ textTransform: 'none' }} />
                </Tabs>
            </ThemeProvider>
        </div>
    );
};

interface IOneWaySearchTabProps {
    oneWayVehicleList?: IVehicleSearch[];
    setSearchResults: Function;
}

export const OneWaySearchTab = ({ oneWayVehicleList, setSearchResults }: IOneWaySearchTabProps) => {
    // for one-way reservation tab sorting only, lighter functionality with tab sort for distance, price and volume only
    const [originalList, setOriginalList] = useState<IVehicleSearch[]>(oneWayVehicleList ?? []);
    const [lowestPrice, setLowestPrice] = useState<number>(0);
    const [lowestDistance, setLowestDistance] = useState<number>(0);
    const [highestTrunkVolume, setHighestTrunkVolume] = useState<number>(0);
    const [value, setValue] = useState( "distance");

    useEffect(() => {
        if (oneWayVehicleList) { setOriginalList(oneWayVehicleList)}
    }, [oneWayVehicleList])

    useEffect(() => {
        if(originalList && originalList.length !== 0){
            const lowestDistance = originalList?.reduce((prev, current) => (prev.distance < current.distance) ? prev : current);
            setLowestDistance(Math.round((+lowestDistance.distance / 1000) * 10) / 10);
            const lowestPriceVehicle = originalList?.reduce((prev: any, current: any) => (prev.pricingData.base.totalPrice < current.pricingData.base.totalPrice) ? prev : current);
            if (lowestPriceVehicle) {
                setLowestPrice(basePrice(lowestPriceVehicle));
            }
            const highestVolumeVehicle = originalList?.reduce((prev: any, current: any) => (prev.vehicleModelData?.dimensions?.trunkVolume < current.vehicleModelData?.dimensions?.trunkVolume) ? current : prev);
            if (highestVolumeVehicle?.vehicleModelData?.dimensions?.trunkVolume !== undefined) {
                setHighestTrunkVolume(highestVolumeVehicle.vehicleModelData.dimensions.trunkVolume);
            }
        }
    }, [oneWayVehicleList]);

    const sortVehicles = (option: SortOptions) => {
        let sortedVehicles: IVehicleSearch[] = [...(originalList || [])]; // provide a default empty array when filteredVehicles is undefined
        sortedVehicles.sort((a, b) => {
            // Sort by reserved status first
            if (a.availabilityData?.available && !b.availabilityData?.available) return -1; // if a is reserved and b is not, sort a to the end
            if (!a.availabilityData?.available && b.availabilityData?.available) return 1; // if b is reserved and a is not, sort b to the end
            // Then sort by the selected option
            switch (option) {
                case SortOptions.Distance:
                    return a.distance - b.distance;
                case SortOptions.Price:
                    return a.pricingData.base.totalPrice - b.pricingData.base.totalPrice;
                case SortOptions.TrunkVolume:
                    return b.vehicleModelData!.dimensions!.trunkVolume! - a.vehicleModelData!.dimensions!.trunkVolume!;
            }
        });

        setSearchResults(sortedVehicles);
        return sortedVehicles;
    };

    const handleChange = (event: React.ChangeEvent<{}>, newValue: string) => {
        setValue(newValue);
        if(newValue === 'distance') {
            // setSearchResults(mapData);
            sortVehicles('distance' as SortOptions)
        }
        if(newValue === 'price') {
            sortVehicles('price' as SortOptions)
        }
        if(newValue === 'trunkvolume') {
            sortVehicles('trunkvolume' as SortOptions)
        }
    };

    return (
        <div className={'mt-3'}>
            <ThemeProvider theme={yellowTheme}>
                <Tabs value={value} onChange={handleChange} variant="fullWidth"  TabIndicatorProps={{ style: { backgroundColor: "white" } }}>
                    <Tab label={`${translateService.translate('vehicleSearch.closest')} ${lowestDistance} km`} value="distance" classes={{ selected: 'tabActive', root: 'tabInactive' }} sx={{ textTransform: 'none' }}/>
                    <Tab label={`${translateService.translate('vehicleSearch.cheapest')} ${lowestPrice} €`} value="price" classes={{ selected: 'tabActive', root: 'tabInactive' }} sx={{ textTransform: 'none' }} />
                    <Tab label={`${translateService.translate('vehicleSearch.trunkSize')} ${highestTrunkVolume} m³`} value="trunkvolume" classes={{ selected: 'tabActive', root: 'tabInactive' }} sx={{ textTransform: 'none' }} />
                </Tabs>
            </ThemeProvider>
        </div>
    );
};